import { useMemo } from 'react'

import Icon from '../icon/Icon'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useListingDecisions } from '../../_hooks/use-listing-decisions'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { buildRenterInsights } from './helpers'
import {
  Section,
  SectionHeader,
  SectionTitle,
  InsightList,
  InsightRow,
  Glyph,
  Body,
  Title,
  Text,
  Cta,
} from './styled'
import { SmartInsightsProps } from './types'

const DEFAULT_MAX_TO_SHOW = 2

/**
 * Renter-pattern insights derived from their profile + listings + tags.
 * Surfaces up to two at a time so the dashboard stays scannable. Renders
 * nothing when there's nothing meaningful to say.
 *
 * @param {SmartInsightsProps} props
 * @returns {JSX.Element | null}
 */
const SmartInsights = ({
  shouldShow = true,
  maxToShow = DEFAULT_MAX_TO_SHOW,
}: SmartInsightsProps): JSX.Element | null => {
  const { profile } = useQuickPassProfile()
  const { listings } = useImportedListings()
  const { tags } = useListingDecisions()

  const insights = useMemo(
    () => buildRenterInsights(profile, listings, tags),
    [profile, listings, tags],
  )

  if (shouldShow === false) {
    return null
  }

  if (insights.length === 0) {
    return null
  }

  const visible = insights.slice(0, maxToShow)

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Smart insights</SectionTitle>
      </SectionHeader>
      <InsightList>
        {visible.map(insight => (
          <InsightRow key={insight.id} severity={insight.severity}>
            <Glyph aria-hidden="true">
              <Icon icon={insight.glyph} />
            </Glyph>
            <Body>
              <Title>{insight.title}</Title>
              <Text>{insight.body}</Text>
              {insight.callToAction !== null &&
              insight.callToActionPath !== null ? (
                <Cta to={insight.callToActionPath}>
                  {insight.callToAction} →
                </Cta>
              ) : null}
            </Body>
          </InsightRow>
        ))}
      </InsightList>
    </Section>
  )
}

export default SmartInsights
